// React Dependencies
import React, { useEffect, useState } from 'react';

// External Dependencies
import _ from 'lodash';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { DndContext, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { Button, Grid, Paper, Typography } from '@material-ui/core';
import { toast } from 'react-toastify';
import { connect } from 'react-redux';

// Internal Dependencies
import GridList from './gridList';
import apiService from '../../../../services.js/apiService';
import { updateTag } from '../../../../actions/admin/tagAction';
import { IsEmpty } from '../../../../common/checks';

const useStyles = makeStyles((theme) => ({
  sort: {
    zIndex: 1000000,
    cursor: 'grabbing',
  },
  paper: {
    padding:   theme.spacing(1),
    textAlign: 'center',
    color:     theme.palette.text.secondary,
  },
}));

const HeaderTitle = 'Display First In App';
const FooterTitle = 'Display Last In App';

const Sortable = ({ getNumberOfTrendingTags, updateList, updateLower, updateTagAction }) => {
  const classes = useStyles();
  const sensors = useSensors(
    useSensor(TouchSensor),
    useSensor(MouseSensor),
  );

  const [tagData, setTagData] = useState([]);
  const [sorted, setSorted] = useState([]);
  const [showSave, setShowSave] = useState(false);

  const maxTrendingTags = async (value) => {
    try {
      getNumberOfTrendingTags(value);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const shouldUpdateLowerTable = (value) => {
    updateLower(value);
  };

  const fetchTagsWithTrendRank = async () => {
    try {
      const approvedTags = await apiService.getTagsWithTrendRank();
      if (approvedTags) {
        const trendTags = _.map(approvedTags, (d, index) => _.assign({
          id:         `${new Date().getTime()}-index-${index + 1}`, // we need this "id" key in data, because @dnd-kit relies on this to display animaton of dragging
          name:       d.name,
          isApproved: d.isApproved,
          trendRank:  d.trendRank,
        }));
        maxTrendingTags(trendTags.length);
        setTagData(trendTags);
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  const removeFromTrending = (tag) => {
    try {
      const p = new Promise((resolve, reject) => {
        const sendUpdate = updateTagAction({ name: tag, trendRank: null }, tag);
        if (sendUpdate) {
          resolve(sendUpdate);
          shouldUpdateLowerTable(true);
        } else {
          reject('An unexpected error has occured');
        }
      });

      p.then(() => {
        toast.success(`Removed ${tag} From Trending List`);
        fetchTagsWithTrendRank();
      }).catch((err) => {
        console.log(err);
      });
    } catch (error) {
      console.log(error);
    }
  };

  const handleSort = async (data) => {
    try {
      if (data) {
        const sortTags = _.map(data, (d) => _.assign({
          name:       d.name,
          isApproved: d.isApproved,
          trendRank:  d.trendRank,
        }));
        setSorted(sortTags);
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  const saveOrder = async () => {
    try {
      await apiService.updateTagsTrendRank(sorted);
      setShowSave(false);
      toast.success('Display Order Updated');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (IsEmpty(active) || IsEmpty(over)) {
      return;
    }

    if (active.id !== over.id) {
      setTagData((_items) => {
        const oldIndex = _items.findIndex((item) => item.id === active.id);
        const newIndex = _items.findIndex((item) => item.id === over.id);

        const sortedTags = arrayMove(_items, oldIndex, newIndex);
        handleSort(sortedTags);

        return sortedTags;
      });

      setShowSave(true);
    }
  };

  useEffect(() => {
    fetchTagsWithTrendRank();
  }, [updateList]);

  useEffect(() => {
    fetchTagsWithTrendRank();
  }, []);

  return (
    <>
      <Grid container item xs={12}>
        <>
          <Grid item xs={12} style={{ flexDirection: 'row' }}>
            <Paper className={classes.paper} elevation={0}>
              <Typography variant="subtitle2">{HeaderTitle.toUpperCase()}</Typography>
            </Paper>
          </Grid>
        </>
      </Grid>
      <hr />

      { !IsEmpty(tagData) ? (
        <DndContext onDragEnd={handleDragEnd} sensors={sensors} modifiers={[restrictToVerticalAxis]}>
          <SortableContext
            items={tagData}
            strategy={verticalListSortingStrategy}
          >
            {tagData.map((value) => (
              <div key={`item-${value.id}`} className={classes.sort}>
                <GridList value={value} removeFromTrending={removeFromTrending}>
                  <Button
                    onClick={() => removeFromTrending(value.name)}
                    color="secondary"
                    variant="contained"
                    style={{ textTransform: 'none', width: 100 }}
                    size="small"
                  >
                    Remove
                  </Button>
                </GridList>
              </div>
            ))}
          </SortableContext>
        </DndContext>
      ) : <p className="text-center font-weight-bold">Please Add a Trending Tag</p>}
      <hr />

      <Grid container item xs={12}>
        <>
          <Grid item xs={12}>
            <center>
              <Paper className={classes.paper} elevation={0}>
                <Typography variant="subtitle2">{FooterTitle.toUpperCase()}</Typography>
              </Paper>
              { showSave
            && (
              <Button
                onClick={() => saveOrder()}
                color="primary"
                variant="contained"
                style={{ textTransform: 'none', width: 150 }}
                size="medium"
              >
                Save Display Position
              </Button>
            )}
            </center>
          </Grid>
        </>
      </Grid>
    </>
  );
};

Sortable.propTypes = {
  getNumberOfTrendingTags: PropTypes.func.isRequired,
  updateList:              PropTypes.bool.isRequired,
  updateLower:             PropTypes.func.isRequired,
  updateTagAction:         PropTypes.func.isRequired,
};

// map props to redux state (use Chrome Redux DevTools to help identify how state are stored)
function mapStateToProps(state) {
  return {
    tags: state.admin.tag.tags,
  };
}

// map the redux action creators to props
const mapActionToProps = {
  updateTagAction: updateTag,
};

// connect(mapStateToProps, actions to component as props)(component)
export default connect(
  mapStateToProps,
  mapActionToProps,
)(Sortable);
